import React, { useState } from "react";
import api from "../../api/api";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    InputAdornment,
} from "@mui/material";

import {
    Email,
    VolunteerActivism,
    ArrowBack,
    ArrowForward,
    VerifiedUser,
    Security,
    LockReset,
    Favorite,
    Public,
    Groups,
} from "@mui/icons-material";

/* =========================================================
   PASSION AIDS THE FUTURE COLORS
========================================================= */

const COLORS = {
    navy: "#102A43",
    deepNavy: "#071A2B",
    blue: "#1769AA",
    brightBlue: "#2185D0",

    green: "#16834B",
    brightGreen: "#22A861",

    gold: "#E5A91A",
    brightGold: "#F4C542",

    white: "#FFFFFF",
    cream: "#F7F9FB",
    softBlue: "#F1F7FC",
    paleGreen: "#EEF8F2",

    text: "#172B3A",
    muted: "#6B7A86",
    border: "#DCE5EC",

    red: "#B42318",
    paleRed: "#FEF3F2",
};

/* =========================================================
   FLOATING ICON
========================================================= */

const FloatingIcon = ({
    Icon,
    top,
    left,
    right,
    bottom,
    delay = 0,
    size = 28,
}) => (
    <motion.div
        initial={{
            opacity: 0,
            y: 20,
            scale: 0.7,
        }}
        animate={{
            opacity: [0, 0.65, 0.35, 0],
            y: [20, -5, -30, -60],
            x: [0, 10, -5, 15],
            rotate: [0, 8, -8, 12],
            scale: [0.7, 1, 1.05, 0.65],
        }}
        transition={{
            duration: 8,
            delay,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            top,
            left,
            right,
            bottom,
            zIndex: 3,
            pointerEvents: "none",
        }}
    >
        <Icon
            sx={{
                fontSize: size,
                color: COLORS.brightGold,
                opacity: 0.65,
                filter:
                    "drop-shadow(0 0 10px rgba(244,197,66,.22))",
            }}
        />
    </motion.div>
);

/* =========================================================
   BACKGROUND GLOW
========================================================= */

const Glow = ({
    size = 400,
    top,
    left,
    right,
    bottom,
    color = "rgba(33,133,208,.14)",
}) => (
    <motion.div
        animate={{
            scale: [0.92, 1.08, 0.92],
            opacity: [0.35, 0.6, 0.35],
        }}
        transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            width: size,
            height: size,
            top,
            left,
            right,
            bottom,
            borderRadius: "50%",
            background: `radial-gradient(
                circle,
                ${color} 0%,
                transparent 70%
            )`,
            pointerEvents: "none",
            zIndex: 1,
        }}
    />
);

/* =========================================================
   TECHNICAL / ORGANIZATION GRID
========================================================= */

const BackgroundGrid = () => (
    <Box
        sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.055,
            pointerEvents: "none",
            backgroundImage: `
                linear-gradient(
                    rgba(255,255,255,.7) 1px,
                    transparent 1px
                ),
                linear-gradient(
                    90deg,
                    rgba(255,255,255,.7) 1px,
                    transparent 1px
                )
            `,
            backgroundSize: "42px 42px",
            maskImage:
                "linear-gradient(to bottom, black, transparent 92%)",
        }}
    />
);

/* =========================================================
   FORGOT PASSWORD
========================================================= */

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    /* =====================================================
       REQUEST RESET CODE
    ===================================================== */

    const handleRequest = async (e) => {
        e.preventDefault();

        const cleanEmail = email.trim();

        if (!cleanEmail) {
            setMessage(
                "Please enter the email address associated with your account."
            );
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const res = await api.post(
                "/request-reset-code/",
                {
                    email: cleanEmail,
                }
            );

            setMessage(
                res.data.message ||
                    "Verification code sent successfully."
            );

            navigate("/verifyCode", {
                state: {
                    email: cleanEmail,
                },
            });
        } catch (err) {
            setMessage(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    "We could not send the verification code. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                width: "100%",
                background: COLORS.cream,
                overflow: "hidden",
                position: "relative",
            }}
        >
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: {
                        xs: "block",
                        md: "grid",
                    },
                    gridTemplateColumns: {
                        md: "1.08fr .92fr",
                    },
                }}
            >
                {/* =====================================================
                    LEFT BRAND PANEL
                ===================================================== */}

                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                        position: "relative",
                        minHeight: "100dvh",
                        overflow: "hidden",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        px: {
                            md: 6,
                            lg: 8,
                            xl: 10,
                        },
                        py: 5,
                        color: COLORS.white,

                        background:
                            "linear-gradient(135deg, #071A2B 0%, #102A43 48%, #124D3A 100%)",
                    }}
                >
                    <BackgroundGrid />

                    <Glow
                        size={650}
                        top={-350}
                        left={-300}
                        color="rgba(33,133,208,.18)"
                    />

                    <Glow
                        size={500}
                        bottom={-300}
                        right={-250}
                        color="rgba(229,169,26,.13)"
                    />

                    {/* FLOATING ICONS */}

                    <FloatingIcon
                        Icon={VolunteerActivism}
                        top="18%"
                        left="9%"
                        delay={0}
                        size={31}
                    />

                    <FloatingIcon
                        Icon={Groups}
                        top="32%"
                        right="11%"
                        delay={1}
                        size={29}
                    />

                    <FloatingIcon
                        Icon={Public}
                        top="49%"
                        left="7%"
                        delay={2}
                        size={30}
                    />

                    <FloatingIcon
                        Icon={Favorite}
                        top="66%"
                        right="10%"
                        delay={3}
                        size={29}
                    />

                    <FloatingIcon
                        Icon={Security}
                        top="77%"
                        left="20%"
                        delay={4}
                        size={28}
                    />

                    {/* =================================================
                        BRAND HEADER
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "14px",

                                background:
                                    "linear-gradient(135deg, #1769AA, #22A861)",

                                boxShadow:
                                    "0 12px 30px rgba(0,0,0,.3)",
                            }}
                        >
                            <VolunteerActivism
                                sx={{
                                    fontSize: 29,
                                    color: COLORS.white,
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    fontSize: ".72rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.8,
                                    color: COLORS.brightGold,
                                }}
                            >
                                PASSION
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: ".9rem",
                                    fontWeight: 850,
                                    letterSpacing: 1,
                                    color:
                                        "rgba(255,255,255,.86)",
                                }}
                            >
                                AIDS THE FUTURE
                            </Typography>
                        </Box>
                    </Box>

                    {/* =================================================
                        MAIN BRAND CONTENT
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            maxWidth: 680,
                            mt: -5,
                        }}
                    >
                        {/* LABEL */}

                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.8,
                                px: 1.7,
                                py: 0.8,
                                mb: 2.5,
                                borderRadius: "6px",

                                background:
                                    "rgba(229,169,26,.12)",

                                border:
                                    "1px solid rgba(229,169,26,.28)",
                            }}
                        >
                            <LockReset
                                sx={{
                                    fontSize: 16,
                                    color:
                                        COLORS.brightGold,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".62rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.7,
                                    color:
                                        COLORS.brightGold,
                                }}
                            >
                                ACCOUNT RECOVERY
                            </Typography>
                        </Box>

                        {/* HEADING */}

                        <Typography
                            sx={{
                                fontSize: {
                                    md: "3.3rem",
                                    lg: "4.2rem",
                                    xl: "4.8rem",
                                },
                                fontWeight: 1000,
                                lineHeight: 0.98,
                                letterSpacing: "-2.7px",
                                textTransform: "uppercase",
                            }}
                        >
                            Welcome
                            <br />
                            Back To Your
                            <Box
                                component="span"
                                sx={{
                                    display: "block",
                                    color:
                                        COLORS.brightGold,
                                }}
                            >
                                Journey.
                            </Box>
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 560,
                                mt: 3,
                                color:
                                    "rgba(255,255,255,.68)",
                                fontSize: "1rem",
                                lineHeight: 1.8,
                            }}
                        >
                            Forgot your password? Don't worry.
                            Enter the email connected to your
                            Passion Aids The Future account and
                            we'll help you securely restore access.
                        </Typography>

                        {/* =================================================
                            RECOVERY STEPS
                        ================================================= */}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(3, 1fr)",
                                gap: 1.2,
                                maxWidth: 600,
                                mt: 4,
                            }}
                        >
                            {[
                                {
                                    number: "01",
                                    title: "EMAIL",
                                },
                                {
                                    number: "02",
                                    title: "VERIFY",
                                },
                                {
                                    number: "03",
                                    title: "RESTORE",
                                },
                            ].map((item) => (
                                <Box
                                    key={item.number}
                                    sx={{
                                        px: 1.5,
                                        py: 1.5,
                                        borderRadius: "10px",

                                        background:
                                            "rgba(255,255,255,.045)",

                                        border:
                                            "1px solid rgba(255,255,255,.08)",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: ".6rem",
                                            fontWeight: 950,
                                            color:
                                                COLORS.brightGold,
                                            letterSpacing: 1,
                                        }}
                                    >
                                        {item.number}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.4,
                                            fontSize: ".72rem",
                                            fontWeight: 900,
                                            color:
                                                "rgba(255,255,255,.78)",
                                        }}
                                    >
                                        {item.title}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* =================================================
                        LEFT FOOTER
                    ================================================= */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            borderTop:
                                "1px solid rgba(255,255,255,.10)",
                            pt: 2.5,
                            display: "flex",
                            justifyContent:
                                "space-between",
                            gap: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: ".63rem",
                                fontWeight: 800,
                                letterSpacing: 1.2,
                                color:
                                    "rgba(255,255,255,.43)",
                            }}
                        >
                            HOPE • SERVICE • IMPACT
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: ".63rem",
                                fontWeight: 800,
                                color:
                                    "rgba(255,255,255,.43)",
                                textAlign: "right",
                            }}
                        >
                            PASSION AIDS THE FUTURE
                        </Typography>
                    </Box>
                </Box>

                {/* =====================================================
                    RIGHT RECOVERY PANEL
                ===================================================== */}

                <Box
                    sx={{
                        minHeight: "100dvh",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",

                        px: {
                            xs: 2,
                            sm: 4,
                            md: 5,
                            lg: 7,
                        },

                        py: {
                            xs: 6,
                            md: 5,
                        },

                        background:
                            "linear-gradient(150deg, #FFFFFF 0%, #F5F9FC 100%)",
                    }}
                >
                    <Glow
                        size={400}
                        top={-230}
                        right={-220}
                        color="rgba(33,133,208,.09)"
                    />

                    <Glow
                        size={330}
                        bottom={-200}
                        left={-190}
                        color="rgba(22,131,75,.08)"
                    />

                    {/* =================================================
                        MOBILE BRAND
                    ================================================= */}

                    <Box
                        sx={{
                            display: {
                                xs: "flex",
                                md: "none",
                            },

                            position: "absolute",
                            top: 18,
                            left: 18,
                            right: 18,

                            alignItems: "center",
                            justifyContent:
                                "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems:
                                    "center",
                                gap: 0.9,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    borderRadius:
                                        "10px",

                                    background:
                                        COLORS.navy,
                                }}
                            >
                                <VolunteerActivism
                                    sx={{
                                        color:
                                            COLORS.brightGold,
                                        fontSize: 22,
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: ".66rem",
                                    fontWeight: 950,
                                    letterSpacing: 0.9,
                                    color:
                                        COLORS.navy,
                                }}
                            >
                                PASSION AIDS THE FUTURE
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                px: 1.2,
                                py: 0.55,
                                borderRadius: "20px",

                                background:
                                    COLORS.paleGreen,

                                color:
                                    COLORS.green,

                                fontSize: ".58rem",
                                fontWeight: 900,
                            }}
                        >
                            SECURE
                        </Box>
                    </Box>

                    {/* =================================================
                        FORM AREA
                    ================================================= */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            x: 35,
                        }}
                        animate={{
                            opacity: 1,
                            x: 0,
                        }}
                        transition={{
                            duration: 0.7,
                            ease: "easeOut",
                        }}
                        style={{
                            position: "relative",
                            zIndex: 10,
                            width: "100%",
                            maxWidth: 455,
                        }}
                    >
                        {/* =================================================
                            FORM HEADER
                        ================================================= */}

                        <Box
                            sx={{
                                textAlign: "center",
                                mb: 3,
                            }}
                        >
                            <motion.div
                                animate={{
                                    y: [0, -7, 0],
                                    rotate: [
                                        0,
                                        2,
                                        -2,
                                        0,
                                    ],
                                }}
                                transition={{
                                    duration: 3.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 70,
                                        height: 70,
                                        mx: "auto",
                                        mb: 2,

                                        display: "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",

                                        borderRadius:
                                            "20px",

                                        background:
                                            "linear-gradient(135deg, #102A43, #1769AA)",

                                        boxShadow:
                                            "0 18px 35px rgba(16,42,67,.18)",

                                        position:
                                            "relative",
                                    }}
                                >
                                    <LockReset
                                        sx={{
                                            fontSize: 36,
                                            color:
                                                COLORS.white,
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            position:
                                                "absolute",
                                            width: 13,
                                            height: 13,
                                            right: -4,
                                            top: -4,
                                            borderRadius:
                                                "50%",
                                            background:
                                                COLORS.brightGold,
                                            border:
                                                "3px solid #F7F9FB",
                                        }}
                                    />
                                </Box>
                            </motion.div>

                            <Typography
                                sx={{
                                    fontSize: ".67rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.8,
                                    color:
                                        COLORS.blue,
                                }}
                            >
                                PASSION AIDS THE FUTURE
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.7,
                                    fontSize: {
                                        xs: "1.9rem",
                                        sm: "2.25rem",
                                    },
                                    fontWeight: 1000,
                                    letterSpacing: "-1px",
                                    color:
                                        COLORS.navy,
                                    lineHeight: 1.05,
                                }}
                            >
                                Forgot Password?
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 1,
                                    color:
                                        COLORS.muted,
                                    fontSize: ".86rem",
                                    lineHeight: 1.65,
                                    maxWidth: 390,
                                    mx: "auto",
                                }}
                            >
                                Enter your account email to
                                receive a secure verification
                                code and restore access to your
                                account.
                            </Typography>
                        </Box>

                        {/* =================================================
                            FORM CARD
                        ================================================= */}

                        <Box
                            sx={{
                                position: "relative",

                                p: {
                                    xs: 2.3,
                                    sm: 3.5,
                                },

                                borderRadius: "24px",

                                background:
                                    "rgba(255,255,255,.96)",

                                border:
                                    `1px solid ${COLORS.border}`,

                                boxShadow:
                                    "0 25px 70px rgba(16,42,67,.10)",

                                backdropFilter:
                                    "blur(18px)",
                            }}
                        >
                            {/* SECURITY HEADER */}

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.2,
                                    mb: 2.5,
                                    pb: 2,

                                    borderBottom:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "10px",

                                        display: "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",

                                        background:
                                            COLORS.softBlue,
                                    }}
                                >
                                    <VerifiedUser
                                        sx={{
                                            fontSize: 20,
                                            color:
                                                COLORS.blue,
                                        }}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: ".76rem",
                                            fontWeight: 950,
                                            color:
                                                COLORS.navy,
                                        }}
                                    >
                                        SECURE ACCOUNT RECOVERY
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.25,
                                            fontSize: ".64rem",
                                            color:
                                                COLORS.muted,
                                        }}
                                    >
                                        Verification code required
                                    </Typography>
                                </Box>
                            </Box>

                            {/* =================================================
                                FORM
                            ================================================= */}

                            <Box
                                component="form"
                                onSubmit={handleRequest}
                            >
                                <Typography
                                    sx={{
                                        mb: 0.75,
                                        fontSize: ".68rem",
                                        fontWeight: 900,
                                        color:
                                            COLORS.text,
                                    }}
                                >
                                    EMAIL ADDRESS
                                </Typography>

                                <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    placeholder="Enter your email address"
                                    value={email}
                                    disabled={loading}
                                    onChange={(e) =>
                                        setEmail(
                                            e.target.value
                                        )
                                    }
                                    autoComplete="email"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email
                                                    sx={{
                                                        fontSize: 20,
                                                        color:
                                                            COLORS.blue,
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        mb: 2.5,

                                        "& .MuiOutlinedInput-root":
                                            {
                                                minHeight: 56,
                                                borderRadius:
                                                    "12px",
                                                background:
                                                    "#FBFDFC",

                                                "& fieldset":
                                                    {
                                                        borderColor:
                                                            COLORS.border,
                                                    },

                                                "&:hover fieldset":
                                                    {
                                                        borderColor:
                                                            "#9CBFD9",
                                                    },

                                                "&.Mui-focused fieldset":
                                                    {
                                                        borderColor:
                                                            COLORS.blue,
                                                        borderWidth:
                                                            1.5,
                                                    },
                                            },

                                        "& input": {
                                            fontSize:
                                                ".88rem",
                                        },
                                    }}
                                />

                                {/* SUBMIT */}

                                <Button
                                    fullWidth
                                    type="submit"
                                    disabled={loading}
                                    sx={{
                                        minHeight: 57,
                                        borderRadius: "12px",

                                        textTransform:
                                            "none",

                                        fontSize: ".9rem",
                                        fontWeight: 950,

                                        color:
                                            COLORS.white,

                                        background:
                                            `linear-gradient(
                                                135deg,
                                                ${COLORS.navy},
                                                ${COLORS.blue}
                                            )`,

                                        boxShadow:
                                            "0 14px 28px rgba(16,42,67,.17)",

                                        transition:
                                            "all .25s ease",

                                        "&:hover": {
                                            background:
                                                `linear-gradient(
                                                    135deg,
                                                    ${COLORS.blue},
                                                    ${COLORS.green}
                                                )`,

                                            transform:
                                                "translateY(-1px)",

                                            boxShadow:
                                                "0 18px 34px rgba(16,42,67,.22)",
                                        },

                                        "&:disabled": {
                                            color:
                                                COLORS.white,

                                            background:
                                                "#93A39A",
                                        },
                                    }}
                                >
                                    {loading ? (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                gap: 1.2,
                                            }}
                                        >
                                            <CircularProgress
                                                size={20}
                                                thickness={5}
                                                sx={{
                                                    color:
                                                        COLORS.white,
                                                }}
                                            />

                                            Sending Code...
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                gap: 0.8,
                                            }}
                                        >
                                            Send Verification Code

                                            <ArrowForward
                                                sx={{
                                                    fontSize:
                                                        18,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Button>

                                {/* BACK TO LOGIN */}

                                <Button
                                    fullWidth
                                    startIcon={
                                        <ArrowBack />
                                    }
                                    onClick={() =>
                                        navigate(
                                            "/login"
                                        )
                                    }
                                    sx={{
                                        mt: 1.3,
                                        minHeight: 48,

                                        color:
                                            COLORS.muted,

                                        fontSize: ".78rem",
                                        fontWeight: 800,

                                        textTransform:
                                            "none",

                                        borderRadius:
                                            "10px",

                                        "&:hover": {
                                            background:
                                                COLORS.softBlue,
                                            color:
                                                COLORS.navy,
                                        },
                                    }}
                                >
                                    Back to Login
                                </Button>
                            </Box>

                            {/* =================================================
                                RESPONSE MESSAGE
                            ================================================= */}

                            {message && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 1.5,
                                        borderRadius:
                                            "10px",

                                        background:
                                            message
                                                .toLowerCase()
                                                .includes(
                                                    "sent"
                                                )
                                                ? COLORS.paleGreen
                                                : COLORS.paleRed,

                                        border:
                                            message
                                                .toLowerCase()
                                                .includes(
                                                    "sent"
                                                )
                                                ? "1px solid rgba(22,131,75,.14)"
                                                : "1px solid rgba(180,35,24,.14)",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            textAlign:
                                                "center",

                                            fontSize:
                                                ".75rem",

                                            fontWeight:
                                                700,

                                            lineHeight:
                                                1.5,

                                            color:
                                                message
                                                    .toLowerCase()
                                                    .includes(
                                                        "sent"
                                                    )
                                                    ? COLORS.green
                                                    : COLORS.red,
                                        }}
                                    >
                                        {message}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* =================================================
                            RIGHT FOOTER
                        ================================================= */}

                        <Box
                            sx={{
                                mt: 2.5,
                                textAlign: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        "center",
                                    alignItems:
                                        "center",
                                    gap: 0.7,
                                    mb: 0.7,
                                }}
                            >
                                <VolunteerActivism
                                    sx={{
                                        fontSize: 15,
                                        color:
                                            COLORS.green,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontSize: ".61rem",
                                        fontWeight: 900,
                                        letterSpacing: 0.7,
                                        color:
                                            COLORS.green,
                                    }}
                                >
                                    HOPE • SERVICE • IMPACT
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: ".62rem",
                                    color:
                                        COLORS.muted,
                                }}
                            >
                                Passion Aids The Future
                            </Typography>
                        </Box>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default ForgotPassword;